import type { CheckoutEventsPaymentMethodCardDetails, PaddleEventData } from "@paddle/paddle-js";
import { isNullish } from "@tinyurldev/tu-ui-vue";

import type { SubscriptionData } from "@Modules/subscriptions/Data/Models/SubscriptionModel";

/**
 * The payment method a subscription is billed on, ready to be displayed as it is.
 * The brand is left as it comes, as the table renders it as an icon rather than as a wording.
 */
export interface SubscriptionPaymentMethod {
  brand: string | null
  lastFourNumbers: string
  expirationDate: string
}

/**
 * What a payment method carries before the missing details are filled in.
 */
interface PaymentMethodDetails {
  brand?: string | null
  lastFourNumbers?: string | null
  expirationDate?: string | null
}

/**
 * The stand-ins for the details a payment method does not carry, so that a payment method
 * holding nothing but its brand is still displayed. Wallets and alternative payment methods
 * (Apple Pay, PayPal, ...) come without a card number and an expiration date.
 */
const MISSING_CARD_NUMBER = "";
const MISSING_EXPIRATION_DATE = "—";

/**
 * Fills in whatever the given details do not carry.
 */
function buildPaymentMethodDetails(details: PaymentMethodDetails): SubscriptionPaymentMethod {
  return {
    brand: details.brand ?? null,
    lastFourNumbers: details.lastFourNumbers ?? MISSING_CARD_NUMBER,
    expirationDate: details.expirationDate ?? MISSING_EXPIRATION_DATE,
  };
}

/**
 * Builds the expiration date of the card used in a completed Paddle checkout, in the
 * "MM/YY" format the subscription table displays.
 */
function buildPaddleExpirationDate(card?: CheckoutEventsPaymentMethodCardDetails): string | null {
  if (isNullish(card) || isNullish(card.expiry_month) || isNullish(card.expiry_year)) {
    return null;
  }

  return `${String(card.expiry_month).padStart(2, "0")}/${String(card.expiry_year).slice(-2)}`;
}

/**
 * Builds the payment method of the given subscription out of its "pm_*" columns.
 *
 * Only the brand is needed for a payment method to be displayed, so an incomplete one still
 * shows up through it, with the missing details standing in as placeholders.
 */
export function buildSubscriptionPaymentMethod(subscription: SubscriptionData): SubscriptionPaymentMethod {
  // The stored year can be four digits long, and only the last two are displayed.
  const [month, year] = subscription.pm_expiration
    ?.replace(/\d{2}(\d{2})$/, "$1")
    .split("/") ?? [];

  return buildPaymentMethodDetails({
    brand: subscription.pm_type,
    lastFourNumbers: subscription.pm_last_four,
    expirationDate: month || year
      ? `${month ? month.padStart(2, "0") : "-"}/${year || "-"}`
      : null,
  });
}

/**
 * Builds the payment method used in a completed Paddle checkout, so it renders exactly like
 * a persisted one.
 *
 * Paddle only exposes the card when the customer paid with one; wallets and alternative
 * payment methods come with their type alone, so they are carried through the brand and
 * displayed by their icon. Answers null when the checkout carries no payment method at all.
 */
export function buildPaddlePaymentMethod(checkoutResponse?: PaddleEventData | null): SubscriptionPaymentMethod | null {
  const methodDetails = checkoutResponse?.data?.payment?.method_details;
  const card = methodDetails?.card;
  const brand = card?.type ?? methodDetails?.type;

  if (isNullish(brand)) {
    return null;
  }

  return buildPaymentMethodDetails({
    brand,
    lastFourNumbers: card?.last4,
    expirationDate: buildPaddleExpirationDate(card),
  });
}

/**
 * Returns true when the payment method is worth displaying, which the brand alone decides.
 */
export function hasPaymentMethod(paymentMethod: SubscriptionPaymentMethod): boolean {
  return !isNullish(paymentMethod.brand);
}
